import { LitElement } from '@lit'

class NovaProductGallery extends LitElement {
  static properties = {
    galleryCarousel: { type: Object, state: true },
    thumbnailCarousel: { type: Object, state: true },
    thumbnailPrevious: { type: Object, state: true },
    thumbnailNext: { type: Object, state: true },
    zoomInOverlay: { type: Object, state: true },
  }

  constructor() {
    super()
    this.galleryCarousel = null
    this.thumbnailCarousel = null
    this.thumbnailPrevious = null
    this.thumbnailNext = null
    this.zoomInOverlay = null
    this.observer = null
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })

    if (this.observer) {
      this.observer.disconnect()
    }
  }

  updated(params) {
    const changedProperties = Array.from(params, ([key, value]) => ({ key, value }))
    if (changedProperties.length === 1 && changedProperties[0].key === 'zoomInOverlay') return

    this.thumbnailCarousel = this.querySelector('.js-thumbnails-carousel')
    this.galleryCarousel = this.querySelector('.js-gallery-carousel')
    this.thumbnailPrevious = this.thumbnailCarousel.shadowRoot.querySelector(
      '.carousel__navigation-button--previous',
    )
    this.thumbnailNext = this.thumbnailCarousel.shadowRoot.querySelector(
      '.carousel__navigation-button--next',
    )
    this.styleDisabledButton(this.thumbnailPrevious)
    this.styleDisabledButton(this.thumbnailNext)
    this.handleThumbnailCarouselClick()
    this.handleGalleryCarouselMove()
    this.handleZoomOnImage(3)
    this.resetCarousel()
    this.initMutationObserver()
    this.galleryCarousel.goToSlide(0)
    this.thumbnailCarousel.goToSlide(0)
  }

  initMutationObserver() {
    if (this.observer) {
      this.observer.disconnect()
    }

    const handleClassChange = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          this.styleDisabledButton(this.thumbnailPrevious)
          this.styleDisabledButton(this.thumbnailNext)
        }
      }
    }

    this.observer = new MutationObserver(handleClassChange)
    if (this.thumbnailPrevious) {
      this.observer.observe(this.thumbnailPrevious, { attributes: true })
    }
    if (this.thumbnailNext) {
      this.observer.observe(this.thumbnailNext, { attributes: true })
    }
  }

  styleDisabledButton(button) {
    const slottedElement = button.querySelector('slot')?.assignedElements()[0] || ''
    if (!slottedElement) return
    button.style.opacity = 1
    if (button.classList.contains('carousel__navigation-button--disabled')) {
      slottedElement.classList.add('disabled')
    } else {
      slottedElement.classList.remove('disabled')
    }
  }

  resetCarousel() {
    const carousels = this.querySelectorAll('sl-carousel')
    for (const carousel of carousels) {
      carousel.goToSlide(0)
    }
  }

  updateGalleryContent(content) {
    if (!content) return
    // Clear existing children
    while (this.firstChild) {
      this.removeChild(this.firstChild)
    }
    // Append new children
    while (content.firstChild) {
      this.appendChild(content.firstChild)
    }
    this.requestUpdate()
  }

  handleThumbnailCarouselClick() {
    if (!this.thumbnailCarousel) return

    const handleEvent = (event) => {
      if (event.type === 'click' || event.key === 'Enter') {
        const card = event.target.closest('.thumbnails-item')
        if (card) {
          this.handleGalleryCarouselDisplay(card)
        }
      }
    }

    this.thumbnailCarousel.addEventListener('click', handleEvent)
    this.thumbnailCarousel.addEventListener('keydown', handleEvent)
  }

  handleGalleryCarouselDisplay(card) {
    if (!this.galleryCarousel || card.classList.contains('active')) return

    const activeCard = this.thumbnailCarousel.querySelector('.active')
    if (activeCard) activeCard.classList.remove('active')

    card.classList.add('active')
    const cardPosition = card.dataset.position
    if (cardPosition) {
      const displayCardIndex = Number(cardPosition) - 1
      this.galleryCarousel.goToSlide(displayCardIndex)
    }
  }

  handleGalleryCarouselMove() {
    if (!this.galleryCarousel || !this.thumbnailCarousel) return

    this.galleryCarousel.addEventListener('sl-slide-change', (event) => {
      const activeGalleryIndex = event.detail.index
      this.handleVideoAutoPlay(event.detail.slide)
      this.thumbnailCarousel.goToSlide(Number(activeGalleryIndex))
      const oldActiveThumbnail = this.thumbnailCarousel.querySelector('.active')
      if (oldActiveThumbnail) oldActiveThumbnail.classList.remove('active')
      const newActiveThumbnail = this.thumbnailCarousel.querySelector(
        `[data-position="${activeGalleryIndex + 1}"]`,
      )
      if (newActiveThumbnail) newActiveThumbnail.classList.add('active')
    })
  }

  handleVideoAutoPlay(slide) {
    const media = slide.querySelector('nova-adaptive-video')
    if (!media) return
    media.play()
  }

  zoomMoveWithHover(image, event, zoomRatio) {
    const ratio = image.height / image.width
    const container = event.target.getBoundingClientRect()
    const xPosition = event.clientX - container.left
    const yPosition = event.clientY - container.top
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`

    this.zoomInOverlay.style.backgroundPosition = `${xPercent} ${yPercent}`
    this.zoomInOverlay.style.backgroundSize = `${image.width * zoomRatio}px`
  }

  handleZoomEvents(image, zoomRatio) {
    this.zoomInOverlay = image.closest('.img').querySelector('.zoom-overlay')
    if (!this.zoomInOverlay) return
    this.zoomInOverlay.classList.remove('hidden')

    const carouselItem = image.closest('.gallery-item')
    if (carouselItem && !carouselItem.classList.contains('zoomed'))
      carouselItem.classList.add('zoomed')

    this.zoomInOverlay.addEventListener(
      'click',
      this.handleOverlayRemove.bind(this, carouselItem),
    )
    this.zoomInOverlay.addEventListener('mousemove', (event) =>
      this.zoomMoveWithHover(image, event, zoomRatio),
    )
    this.zoomInOverlay.addEventListener(
      'mouseleave',
      this.handleOverlayRemove.bind(this, carouselItem),
    )
  }

  handleOverlayRemove(carouselItem) {
    if (
      this.zoomInOverlay &&
      !this.zoomInOverlay.classList.contains('hidden')
    ) {
      this.zoomInOverlay.classList.add('hidden')

      this.zoomInOverlay.removeEventListener(
        'click',
        this.handleOverlayRemove.bind(this, carouselItem),
      )
      this.zoomInOverlay.removeEventListener(
        'mousemove',
        this.zoomMoveWithHover,
      )
      this.zoomInOverlay.removeEventListener(
        'mouseleave',
        this.handleOverlayRemove.bind(this, carouselItem),
      )

      this.zoomInOverlay = null
    }
    if (carouselItem) carouselItem.classList.remove('zoomed')
  }

  handleZoomOnImage(zoomRatio) {
    const images = this.querySelectorAll('.zoom-image')
    for (const image of images) {
      image.addEventListener('click', () => {
        this.handleZoomEvents(image, zoomRatio)
      })
    }
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-product-gallery', NovaProductGallery)
